import { createApi } from "@reduxjs/toolkit/query/react";
import { publicBaseQuery } from "@/api/base/public/publicBaseQuery";
import type { InviteCodeResponseEnvelope, InviteCodeEntity } from "./inviteCodeApi";

function getLocaleHeader(): string {
  if (typeof document === "undefined") return "en";
  const m = document.cookie.match(/(?:^|;\s*)NEXT_LOCALE=([^;]+)/);
  return m?.[1] ? decodeURIComponent(m[1]) : "en";
}

/**
 * Public (unauthenticated) invite-code API.
 *
 * Used by the public registration page: /register/[slug]
 *
 * Backend (example):
 *   GET /api/invite-code/by-url?form_url=test-invite-test
 */
export const inviteCodePublicApi = createApi({
  reducerPath: "inviteCodePublicApi",
  baseQuery: publicBaseQuery(),
  endpoints: (builder) => ({
    getInviteCodeByUrl: builder.query<InviteCodeEntity, { form_url: string }>({
      query: ({ form_url }) => ({
        url: "/api/invite-code/by-url",
        method: "GET",
        params: { form_url },
        headers: {
          locale: getLocaleHeader(),
          // Backend returns only requested fields; registration page needs form_data + code, etc.
          "X-Requested-Fields":
            "id,type,code,settings,form_enabled,form_url,form_name,form_description,form_logo,form_data",
        },
      }),
      transformResponse: (response: InviteCodeResponseEnvelope | InviteCodeEntity) => {
        // Some environments may return either envelope { entity } or the entity directly.
        const anyResp = response as any;
        return (anyResp?.entity ?? anyResp) as InviteCodeEntity;
      },
    }),
  }),
});

export const { useGetInviteCodeByUrlQuery } = inviteCodePublicApi;
